import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Steps } from 'antd';
import styled from 'styled-components';

import { ZoomStatus } from 'types';

const { Step } = Steps;

/** Ordered visual steps of the recording pipeline. */
const PIPELINE_STEPS: Array<{ status: ZoomStatus; i18nKey: string }> = [
  { status: ZoomStatus.MATCHED, i18nKey: 'pages.zoom.status.matched' },
  { status: ZoomStatus.DOWNLOADING, i18nKey: 'pages.zoom.status.downloading' },
  { status: ZoomStatus.UPLOADED, i18nKey: 'pages.zoom.status.uploaded' },
  {
    status: ZoomStatus.AI_PROCESSING,
    i18nKey: 'pages.zoom.status.aiProcessing',
  },
  { status: ZoomStatus.AI_COMPLETED, i18nKey: 'pages.zoom.status.aiCompleted' },
  { status: ZoomStatus.PUBLISHED, i18nKey: 'pages.zoom.status.published' },
];

const stepIndex = (status: ZoomStatus): number =>
  PIPELINE_STEPS.findIndex((step) => step.status === status);

interface IPipelineStepperProps {
  status: ZoomStatus;
  errorMessage?: string | null;
}

/** Horizontal pipeline progress for a LessonRecording (antd Steps wrapper). */
export const PipelineStepper = ({
  status,
  errorMessage,
}: IPipelineStepperProps) => {
  const { t } = useTranslation();

  if (status === ZoomStatus.SKIPPED) {
    return <SkippedPill>{t('pages.zoom.status.skipped')}</SkippedPill>;
  }

  const isFailed = status === ZoomStatus.FAILED;
  const isPublished = status === ZoomStatus.PUBLISHED;
  // FAILED carries no failed-step index; mark the last step as errored.
  const current = isFailed
    ? PIPELINE_STEPS.length - 1
    : Math.max(stepIndex(status), 0);

  let stepsStatus: 'process' | 'finish' | 'error' = 'process';
  if (isFailed) {
    stepsStatus = 'error';
  } else if (isPublished) {
    stepsStatus = 'finish';
  }

  return (
    <Wrapper>
      <Steps
        size="small"
        current={current}
        status={stepsStatus}
        labelPlacement="vertical"
      >
        {PIPELINE_STEPS.map((step, index) => (
          <Step
            key={step.status}
            title={t(step.i18nKey)}
            description={
              isFailed && index === current && errorMessage
                ? errorMessage
                : undefined
            }
          />
        ))}
      </Steps>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  width: 100%;
  overflow-x: auto;

  .ant-steps {
    min-width: 560px;
  }

  .ant-steps-item-description {
    color: #ef4444;
    font-size: 12px;
  }
`;

const SkippedPill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 13px;
  color: #71717a;
  background: #f4f4f5;
`;
